import { useCallback, useRef, useState } from "react";
import Cookies from "js-cookie";

/**
 * Stages a file on the platform for a Local File Upload connection.
 *
 * Both entry points that put a file into the platform use this: creating a source
 * from a new file, and replacing the file behind an existing one. They differ only
 * in what they do with the result, so the transfer itself lives here rather than
 * being written twice.
 *
 * The transfer goes through XMLHttpRequest rather than fetch so progress can be
 * reported and an in-flight upload cancelled — neither is available from fetch
 * without a good deal more machinery.
 */
const useUploadFile = (connectionId) => {
  const [file, setFile] = useState(null);
  const [upload, setUpload] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [progress, setProgress] = useState(null);
  const requestRef = useRef(null);

  const reset = useCallback(() => {
    if (requestRef.current) {
      requestRef.current.abort();
      requestRef.current = null;
    }
    setFile(null);
    setUpload(null);
    setUploadError(null);
    setProgress(null);
  }, []);

  /** Clears the transfer result but keeps the chosen file, so it can be sent again. */
  const clearResult = useCallback(() => {
    setUpload(null);
    setProgress(null);
  }, []);

  const startUpload = useCallback(() => {
    if (!file) return;

    setUploadError(null);
    setProgress(0);

    const body = new FormData();
    // Only sent when a connection is actually in view. Appending it regardless would
    // post the string "undefined", which fails to bind — omitting the field is what
    // lets the server resolve the upload connection itself.
    if (connectionId !== undefined && connectionId !== null) {
      body.append("connectionId", connectionId);
    }
    body.append("file", file);

    const request = new XMLHttpRequest();
    requestRef.current = request;
    request.open("POST", "/api/v1/datasources/uploads");
    // Content-Type is deliberately unset: the browser adds it with the multipart
    // boundary, which cannot be constructed by hand.
    request.setRequestHeader("Authorization", `Bearer ${Cookies.get("token")}`);

    request.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    request.onload = () => {
      requestRef.current = null;
      if (request.status >= 200 && request.status < 300) {
        setProgress(100);
        setUpload(JSON.parse(request.responseText));
      } else {
        setProgress(null);
        setUploadError([
          {
            message:
              request.responseText ||
              `Upload failed with status ${request.status}.`,
          },
        ]);
      }
    };

    request.onerror = () => {
      requestRef.current = null;
      setProgress(null);
      setUploadError([{ message: "The upload could not be completed." }]);
    };

    request.send(body);
  }, [file, connectionId]);

  // A transfer is in flight once progress is reported and until a result lands.
  const uploading = progress !== null && !upload;

  return {
    file,
    setFile,
    upload,
    uploadError,
    setUploadError,
    progress,
    uploading,
    startUpload,
    reset,
    clearResult,
  };
};

export default useUploadFile;
